import { BillingCycleMapper } from "../utils/BillingCycleMapper";
import { SubscriptionQueryUtils } from "../utils/SubscriptionQueryUtils";
import { supabase } from "@/integrations/supabase/supabaseClient";
import { addCycle } from "@/lib/billingSchedule";

/**
 * Service responsible for calculating and updating next payment dates for subscriptions
 */
export class NextPaymentService {
  /**
   * Recalculate and update next payment date for a subscription
   * @param subscriptionId - The subscription ID to update
   * @returns {Promise<void>}
   */
  static async recalculateAndUpdateNextPayment(
    subscriptionId: string,
  ): Promise<void> {
    try {
      const subscription =
        await this.getSubscriptionForPaymentCalculation(subscriptionId);

      if (!this.shouldUpdateNextPaymentDate(subscription)) {
        return;
      }

      const latestPayment = await this.getLatestPaidPayment(subscriptionId);
      if (!latestPayment) {
        return;
      }

      const nextPaymentDate = this.calculateNextPaymentDate(
        subscription,
        latestPayment.payment_date,
      );

      await SubscriptionQueryUtils.updateNextPaymentDate(
        subscriptionId,
        nextPaymentDate,
      );
    } catch (error) {
      console.error(
        `Failed to recalculate next payment for subscription ${subscriptionId}:`,
        error,
      );
      // Don't throw - this should be best-effort
    }
  }

  /**
   * Batch update next payment dates for multiple subscriptions
   * @param subscriptionIds - Array of subscription IDs to update
   * @returns {Promise<void>}
   */
  static async batchRecalculateNextPayments(
    subscriptionIds: string[],
  ): Promise<void> {
    const uniqueIds = Array.from(new Set(subscriptionIds));

    await Promise.all(
      uniqueIds.map((id) => this.recalculateAndUpdateNextPayment(id)),
    );
  }

  /**
   * Get subscription data needed for payment calculation
   * @param subscriptionId - The subscription ID
   * @returns {Promise<any>} Subscription data
   * @throws {Error} If subscription not found
   */
  private static async getSubscriptionForPaymentCalculation(
    subscriptionId: string,
  ) {
    const { data: subscription, error } = await supabase
      .from("subscriptions")
      .select(
        "id, billing_cycle, billing_type, auto_renew, auto_renewal_date, trial_end_date, next_payment_date",
      )
      .eq("id", subscriptionId)
      .single();

    if (error || !subscription) {
      throw new Error(
        `Failed to fetch subscription: ${error?.message || "Not found"}`,
      );
    }

    return subscription;
  }

  /**
   * Check if subscription should have its next payment date updated
   * @param subscription - The subscription data
   * @returns {boolean} True if should update
   */
  private static shouldUpdateNextPaymentDate(subscription: any): boolean {
    if (subscription.auto_renew === false) {
      return false;
    }

    // Skip unscheduled subscriptions (those without a billing cycle)
    // This includes unscheduled PAYG subscriptions with no predictable payment schedule
    if (!subscription.billing_cycle) {
      return false;
    }

    // Validate billing cycle format
    if (
      !BillingCycleMapper.isValidBillingCycleFormat(subscription.billing_cycle)
    ) {
      console.warn(
        `Invalid billing cycle format: ${subscription.billing_cycle}`,
      );
      return false;
    }

    return BillingCycleMapper.isValidScheduledBilling(
      subscription.billing_cycle,
    );
  }

  /**
   * Get the latest paid payment for a subscription
   * @param subscriptionId - The subscription ID
   * @returns {Promise<any | null>} Latest paid payment or null
   */
  private static async getLatestPaidPayment(
    subscriptionId: string,
  ): Promise<any | null> {
    const { data: latestPaid, error } = await supabase
      .from("payment_history")
      .select("payment_date")
      .eq("subscription_id", subscriptionId)
      .eq("status", "paid")
      .order("payment_date", { ascending: false })
      .limit(1)
      .maybeSingle();

    if (error) {
      console.error(
        `Failed to fetch latest payment for subscription ${subscriptionId}:`,
        error,
      );
      return null;
    }

    return latestPaid;
  }

  /**
   * Calculate next payment date based on subscription and last payment
   * @param subscription - The subscription data
   * @param lastPaymentDate - The last payment date string
   * @returns {string} ISO date string for next payment
   */
  private static calculateNextPaymentDate(
    subscription: any,
    lastPaymentDate: string,
  ): string {
    const lastPayment = new Date(lastPaymentDate);

    if (isNaN(lastPayment.getTime())) {
      throw new Error(`Invalid last payment date: ${lastPaymentDate}`);
    }

    const { frequency, customDays } =
      BillingCycleMapper.mapBillingCycleToFrequency(subscription.billing_cycle);

    const nextPayment = addCycle(lastPayment, frequency, customDays);
    return nextPayment.toISOString().split("T")[0];
  }

  /**
   * Calculate the most appropriate next payment date using priority system
   * Priority: Manual (existing next_payment_date) → Auto-renewal → Trial end → Last payment + cycle → System prediction
   * @param subscription - The subscription data with all relevant dates
   * @param latestPaymentDate - The latest paid payment date (optional)
   * @returns {string | null} ISO date string for next payment or null if no suitable date found
   */
  static calculateNextPaymentWithPriority(
    subscription: any,
    latestPaymentDate?: string,
  ): string | null {
    // Priority 1: Manual - Use existing next_payment_date if present and future
    if (subscription.next_payment_date) {
      const manualDate = new Date(subscription.next_payment_date);
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      if (manualDate >= today) {
        return subscription.next_payment_date;
      }
    }

    // Priority 2: Auto-renewal date - Use if present and future
    if (subscription.auto_renewal_date) {
      const autoRenewalDate = new Date(subscription.auto_renewal_date);
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      if (autoRenewalDate >= today) {
        return subscription.auto_renewal_date;
      }
    }

    // Priority 3: Trial end date - Use if present and future
    if (subscription.trial_end_date) {
      const trialEndDate = new Date(subscription.trial_end_date);
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      if (trialEndDate >= today) {
        return subscription.trial_end_date;
      }
    }

    // Priority 4: Last payment + billing cycle - Use if we have a valid billing cycle
    if (latestPaymentDate && subscription.billing_cycle) {
      try {
        return this.calculateNextPaymentDate(subscription, latestPaymentDate);
      } catch (error) {
        console.warn(
          `Failed to calculate next payment from last payment: ${error}`,
        );
      }
    }

    // Priority 5: System prediction (fallback - return null to indicate manual intervention needed)
    return null;
  }

  /**
   * Update next payment date for multiple subscriptions with paid payments
   * @param paymentData - Array of payment objects with subscription_id and status
   * @returns {Promise<void>}
   */
  static async updateNextPaymentsForPaidPayments(
    paymentData: Array<{ subscription_id: string; status: string }>,
  ): Promise<void> {
    const paidSubscriptionIds = paymentData
      .filter((payment) => payment.status === "paid")
      .map((payment) => payment.subscription_id);

    if (paidSubscriptionIds.length === 0) {
      return;
    }

    await this.batchRecalculateNextPayments(paidSubscriptionIds);
  }
}
